import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
// Icons used inside PreviewHeader component
import type { Template } from "../api/endpoints/fetchTemplates";
// API handled via extracted hooks
import { useAcroFormTemplateDetails } from "../hooks/useAcroFormTemplateDetails";
import { buildSectionsFromAcro } from "../data/acroHelper";
import { SkeletonPreview } from "./SkeletonPreview";
// Mutations are created via custom hooks
import { useAuth } from "../contexts/AuthContext";
import { fetchMembers, type UserDetailResponse } from "../api/endpoints/users";
import { useTemplateFormData } from "../hooks/useTemplateFormData";
import { normalizeSectionInputs as normalizeShared, ResolveTarget, SectionInputs as SharedSectionInputs, FormValues as SharedFormValues, KeyValuePair as SharedKeyValuePair } from "../lib/normalizeSectionInputs";
import { collectValuesFromContainer } from "../lib/snapshotValues";
import { collectCurrentKeyValuePairs } from "./acroformpreview/collectCurrentKeyValuePairs";
import { applySectionValues } from "./acroformpreview/applySectionValues";
import { handleSubmitForm } from "./acroformpreview/handleSubmitForm";
import { submitAcroForm as submitAcroFormUtil } from "./acroformpreview/submitAcroForm";
import { handlePublishForm } from "./acroformpreview/handlePublishForm";
import { addRowClickListener } from "./acroformpreview/addRowHandler";
import { useSignatureController, useSignatureFieldHandlers } from "../hooks/useSignature";
import { AcroWithSectionHtml } from "./acroformpreview/AcroWithSectionHtml";
import { AcroWithoutSectionHtml } from "./acroformpreview/AcroWithoutSectionHtml";
import { AcroPreviewHeader } from "./acroformpreview/AcroPreviewHeader";
import { AcroErrorBanner } from "./acroformpreview/AcroErrorBanner";
import { useSaveFormMutation } from "./acroformpreview/useSaveFormMutation";
import { useSubmitFormMutation } from "./acroformpreview/useSubmitFormMutation";
import { usePublishFormMutation } from "./acroformpreview/usePublishFormMutation";
import { injectBadgeStyles } from "./acroformpreview/injectBadgeStyles";

interface AcroFormPreviewProps {
  template: Template;
  onClose: () => void;
  onSaved: (fileId: string) => void;
  onInfo: () => void;
  isInfoPanelOpen: boolean;
}

export function AcroFormPreview({ template, onClose, onSaved, onInfo, isInfoPanelOpen }: AcroFormPreviewProps) {
  const displayTitle = template.fileTitle || template.fileName;
  const isPublishedForm = template.isPublished;
  const hasMemberId = !!template.memberId;
  const { data, isLoading, isError, error } = useAcroFormTemplateDetails({ templateId: template.fileId, userEmail: template.userEmail });
  const { user, isAdmin } = useAuth();
  
  // Fetch member details if memberId exists
  const { data: members = [] } = useQuery<UserDetailResponse[]>({
    queryKey: ["memberlist"],
    queryFn: fetchMembers,
    enabled: !!template.memberId,
  });
  
  // Find member details by memberId
  const memberData = members.find((m) => m.id === template.memberId);
  const memberName = memberData ? `${memberData.firstName || ""} ${memberData.lastName || ""}`.trim() : "";
  const displayTitleWithMember = memberName ? `${displayTitle} - ${memberName}` : displayTitle;
  
  // Only disable save/submit for non-admin users when memberId doesn't exist
  const isSaveDisabled = !isAdmin && !hasMemberId;

  // Local state for save feedback
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimerRef = useRef<number | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  // Track Save Changes intent when triggering native form submission
  const saveClickedRef = useRef(false);

  // Type for key-value pairs using id as key (using shared type below)

  // Shared types
  type FormValues = SharedFormValues;
  type SectionInputs = SharedSectionInputs;
  type KeyValuePair = SharedKeyValuePair;

  // Resolver for Acro: flatten into section 0 (no subsections)
  const resolveTarget: ResolveTarget = () => ({ sectionIndex: 0 });
  const [sectionInputs, setSectionInputs] = useState<AcroSectionInputs>({});


  const { headersHtml, sections } = React.useMemo(() => {
    if (!data?.json) return { headersHtml: "", sections: [] as { id: string; name: string; html: string }[] };
    const built = buildSectionsFromAcro(data.json);
    return { headersHtml: built.headersHtml, sections: built.sections };
  }, [data?.json]);

  // Extract all keys from section HTML for preview lifecycle
  const allHtmlKeys = React.useMemo(() => {
    const keys = new Set<string>();
    const temp = document.createElement("div");
    sections.forEach((s) => {
      temp.innerHTML = s.html;
      const inputs = temp.querySelectorAll("input, textarea, select");
      inputs.forEach((el) => {
        const id = (el.getAttribute("id") || "").trim();
        const dataId = (el.getAttribute("data-id") || "").trim();
        const dataFieldId = (el.getAttribute("data-field-id") || "").trim();
        if (id) keys.add(id);
        if (dataId) keys.add(dataId);
        if (dataFieldId) keys.add(dataFieldId);
      });
    });
    return Array.from(keys);
  }, [sections]);

  // Global map of all preview values keyed by field id/data-id/data-field-id
  const [allPreviewValues, setAllPreviewValues] = useState<Record<string, string>>({});

  // Determine if there are any real sections (excluding the synthetic header step)
  const hasRealSections = React.useMemo(() => sections.some((s) => s.id !== "__header__"), [sections]);

  const sectionCount = sections.length;
  const subsectionCount = 0;
  const fieldCount = React.useMemo(() => {
    const container = document.createElement("div");
    return sections.reduce((acc, s) => {
      container.innerHTML = s.html;
      const inputs = container.querySelectorAll("input, textarea, select");
      return acc + inputs.length;
    }, 0);
  }, [sections]);


  // Track key-value pairs per section for final payload
  type SectionPairs = { [sectionKey: string]: KeyValuePair[] };
  const [sectionPairs, setSectionPairs] = useState<SectionPairs>({});

  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const isFirst = currentSectionIndex === 0;
  const isLast = sectionCount > 0 && currentSectionIndex === sectionCount - 1;
  const currentLabel = `Section ${currentSectionIndex + 1} of ${sectionCount}`;

  // Enable Submit Form button only on last section or if single section
  const isSubmitEnabled = React.useMemo(() => {
    return sectionCount === 1 || isLast;
  }, [sectionCount, isLast]);

  // Signature modal/controller integration
  const {
    isOpen: sigOpen,
    signatureValue,
    setSignatureValue,
    openModal: openSignatureModal,
    closeModal: closeSignatureModal,
    saveSignature,
  } = useSignatureController(".preview-form-content");
  useSignatureFieldHandlers(".preview-form-content", openSignatureModal, currentSectionIndex);

  // Save form mutation (extracted)
  const { mutate, isPending: mutationIsPending } = useSaveFormMutation({
    template,
    user: user ?? null,
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Inject minimal CSS for success/error badges
  useEffect(() => {
    injectBadgeStyles();
  }, []);

  // Collect current input values from the form using id attribute
  const collectCurrentPairs = useCallback(() => {
    return collectCurrentKeyValuePairs(".preview-form-content");
  }, []);

  const submitAcroForm = useCallback(() => {
    submitAcroFormUtil({
      saveClickedRef,
      collectCurrentKeyValuePairs: collectCurrentPairs,
      sectionPairs,
      currentSectionIndex,
      allSectionInputs: sectionInputs,
      allPreviewValues,
      mutate,
    });
  }, [collectCurrentPairs, mutate, sectionPairs, sectionInputs, allPreviewValues, currentSectionIndex]);

  // Submit form mutation (separate from save)
  const submitFormMutation = useSubmitFormMutation({
    template,
    user: user ?? null,
    email: user?.email || "",
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  // Handle Submit Form button click
  const handleSubmitFormClick = useCallback(() => {
    saveClickedRef.current = true;
    handleSubmitForm({
      containerSelector: '.preview-form-content',
      collectCurrentKeyValuePairs: collectCurrentPairs,
      sectionInputs,
      sectionPairs,
      currentSectionIndex,
      submit: (args) => submitFormMutation.mutate(args),
    });
  }, [collectCurrentPairs, submitFormMutation, sectionInputs, sectionPairs, currentSectionIndex]);

  // Publish form mutation
  const publishFormMutation = usePublishFormMutation({
    template,
    email: user?.email || "",
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  // Handle Publish Form button click
  const handlePublishFormClick = useCallback(() => {
    handlePublishForm(() => publishFormMutation.mutate());
  }, [publishFormMutation]);

  // Maintain normalized section inputs (API parity)
  type AcroFormValues = { [name: string]: string };
  type AcroSectionInputs = { [sectionKey: string]: AcroFormValues };


  // Only call useTemplateFormData after useAcroFormTemplateDetails succeeds
  const { data: formDataResponse } = useTemplateFormData({
    templateId: data ? template?.fileId ?? null : null, fileName: template?.fileName ?? ""
  });
  const parsedFormData = React.useMemo(() => {
    try {
      const raw = formDataResponse?.data?.formData;
      if (!raw) return undefined;         
      const parsed = JSON.parse(raw) as unknown;
      return parsed as unknown;
    } catch {
      return undefined;
    }
  }, [formDataResponse]);

  useEffect(() => {
    if (parsedFormData) {
      const normalized = normalizeShared(
        parsedFormData as
        | SectionInputs
        | KeyValuePair[]
        | { [sectionKey: string]: KeyValuePair[] }
        | { [key: string]: { [name: string]: string } },
        resolveTarget
      );
      // Flatten into AcroSectionInputs (use section "0" if not nested)
      const result: AcroSectionInputs = {};
      Object.entries(normalized).forEach(([secKey, vals]) => {
        if (Array.isArray(vals)) return; // safety
        const isFormValues = vals && typeof vals === 'object' && Object.values(vals).every(v => typeof v === 'string');
        if (isFormValues) {
          result[secKey] = vals as unknown as AcroFormValues;
        } else {
          const nested = vals as { [subKey: string]: FormValues };
          const merged: AcroFormValues = {};
          Object.values(nested).forEach(subVals => {
            Object.entries(subVals).forEach(([k, v]) => { merged[k] = v; });
          });
          result[secKey] = merged;
        }
      });
      setSectionInputs(result);
      // Initialize global preview values with all HTML keys (empty), then overlay formData values
      setAllPreviewValues((prev) => {
        const next: Record<string, string> = {};
        allHtmlKeys.forEach((k) => { next[k] = prev[k] ?? ""; });
        Object.values(result).forEach((vals) => {
          Object.entries(vals).forEach(([k, v]) => { next[k] = String(v ?? ""); });
        });
        return next;
      });
      // Also initialize sectionPairs for all sections so save/submit includes every value
      const initialPairs: SectionPairs = {};
      Object.entries(result).forEach(([secKey, formVals]) => {
        const pairs: KeyValuePair[] = Object.entries(formVals).map(([key, val]) => ({ key, value: val }));
        initialPairs[secKey] = pairs;
      });
      setSectionPairs(initialPairs);
    }
  }, [parsedFormData, allHtmlKeys]);

  // One-time delayed prefill after HTML injection (mirror XFA flow)
  const valuesAppliedRef = useRef(false);
  useEffect(() => {
    if (valuesAppliedRef.current) return;
    if (!sectionInputs || Object.keys(sectionInputs).length === 0) return;

    // Wait for DOM to be fully ready
    const applyPrefill = () => {
      const container = document.querySelector('.preview-form-content');
      if (!container) return;

      const currentValues = sectionInputs[String(currentSectionIndex)];
      if (currentValues && Object.keys(currentValues).length > 0) {
        valuesAppliedRef.current = true;
        applySectionValues('.preview-form-content', currentValues);
      }
    };

    // Use longer delay to ensure DOM is fully rendered
    setTimeout(applyPrefill, 500);
  }, [sectionInputs, currentSectionIndex]);

  // Snapshot current section values and key-value pairs by data-field-id
  const snapshotCurrentSection = useCallback(() => {
    const container = document.querySelector('.preview-form-content');
    const values = collectValuesFromContainer(container as Element | null, true) as AcroFormValues;
    const pairs = collectCurrentPairs();
    const sectionKey = String(currentSectionIndex);

    // Save values
    setSectionInputs((prev) => ({ ...prev, [sectionKey]: values }));

    // Save key-value pairs
    setSectionPairs((prev) => ({ ...prev, [sectionKey]: pairs }));
    // Update global preview values with current DOM values
    setAllPreviewValues((prev) => {
      const next = { ...prev };
      Object.entries(values).forEach(([k, v]) => { next[k] = String(v ?? ""); });
      return next;
    });
  }, [currentSectionIndex, collectCurrentPairs]);

  // Apply allPreviewValues to current section DOM (binds latest values)
  const applySectionValuesCb = useCallback(() => {
    applySectionValues('.preview-form-content', allPreviewValues);
  }, [allPreviewValues]);

  // Restore values when changing current section (always bind from allPreviewValues)
  useEffect(() => {
    applySectionValuesCb();
  }, [currentSectionIndex, applySectionValuesCb]);

  const handlePrevious = () => {
    snapshotCurrentSection();
    if (!isFirst) setCurrentSectionIndex(currentSectionIndex - 1);
  };

  const handleNext = () => {
    snapshotCurrentSection();
    if (!isLast) setCurrentSectionIndex(currentSectionIndex + 1);
  };

  // Add Row handler for tables injected via dangerouslySetInnerHTML
  useEffect(() => {
    const cleanup = addRowClickListener();
    return cleanup;
  }, [currentSectionIndex]);

  return (
    <div
      className={`preview-form fixed inset-0 bg-white z-50 flex flex-col transition-all duration-300 ${isInfoPanelOpen ? "backdrop-blur-sm bg-white/95" : ""}`}
    >
      {/* Preview Header  */}
      <AcroPreviewHeader
        displayTitle={displayTitleWithMember}
        onInfo={onInfo}
        onClose={onClose}
        isInfoPanelOpen={isInfoPanelOpen}
      />

      <div className={`flex flex-col flex-auto overflow-hidden transition-all duration-300  ${isInfoPanelOpen ? "opacity-75" : ""}`}>
        {isLoading && <SkeletonPreview />}
        {/* Error  */}
        {isError && (
          <AcroErrorBanner message={error instanceof Error ? error.message : undefined} />
        )}

        {/* with section html */}
        {!isLoading && !isError && hasRealSections && (
          <AcroWithSectionHtml
            sections={sections}
            currentSectionIndex={currentSectionIndex}
            onSelectSection={(idx) => setCurrentSectionIndex(idx)}
            isPublishedForm={isPublishedForm}
            errorText={errorText}
            showSuccess={showSuccess}
            mutationIsPending={mutationIsPending}
            submitFormPending={submitFormMutation.isPending}
            publishFormPending={publishFormMutation.isPending}
            isSubmitEnabled={isSubmitEnabled && (isAdmin || hasMemberId)}
            onSaveChanges={submitAcroForm}
            onSubmitForm={handleSubmitFormClick}
            onPublishForm={handlePublishFormClick}
            sigOpen={sigOpen}
            signatureValue={signatureValue || ""}
            onCloseSignature={closeSignatureModal}
            onChangeSignature={setSignatureValue}
            onSaveSignature={saveSignature}
            isFirst={isFirst}
            isLast={isLast}
            currentLabel={currentLabel}
            onPrevious={handlePrevious}
            onNext={handleNext}
            sectionCount={sectionCount}
            subsectionCount={subsectionCount}
            fieldCount={fieldCount}
            isSaveDisabled={isSaveDisabled}
          />
        )}

        {/* without section html */}
        {!isLoading && !isError && !hasRealSections && (
          <AcroWithoutSectionHtml
            headersHtml={headersHtml}
            isPublishedForm={isPublishedForm}
            errorText={errorText}
            showSuccess={showSuccess}
            mutationIsPending={mutationIsPending}
            submitFormPending={submitFormMutation.isPending}
            publishFormPending={publishFormMutation.isPending}
            onSaveChanges={submitAcroForm}
            onSubmitForm={handleSubmitFormClick}
            onPublishForm={handlePublishFormClick}
            sigOpen={sigOpen}
            signatureValue={signatureValue || ""}
            onCloseSignature={closeSignatureModal}
            onChangeSignature={setSignatureValue}
            onSaveSignature={saveSignature}
            isSaveDisabled={isSaveDisabled}
          />
        )}
      </div>
    </div>
  );
}

export default AcroFormPreview;
